import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, useMenuStore } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import SnButton from "@shared/components/SnButton";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_SFA_JOURNAL_REP_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  // grid1(담당자 헤더행) 선택값 보관 — grid2/3 더블클릭 드릴다운 시 업무영역/상태 소스로 쓴다.
  const headRowRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_sfa_journal_rep_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  //endregion

  //#region onSave
  //#endregion

  const normalizeYm = (value: any) => {
    if (value == null) return "";
    const text = String(value).replace(/\D/g, "");
    return text.length >= 6 ? text.slice(0, 6) : "";
  };

  // 전주실적(grid2)/금주계획(grid3) 행 더블클릭 → 주간업무일지작성(REG_001) 드릴다운.
  // 조회조건: 기준주차(yy,no_week)는 검색패널 값, 담당자·업무영역은 상단 grid1 포커스행(부모 담당자행)에서 가져온다.
  const onDrilldownToReg = (event: any) => {
    const row = event?.data;
    if (!row) return;
    const search = searchRef.current?.getFormdata?.() ?? {};
    // 담당자·업무영역은 상단 grid1(담당자당 1건)에서 가져온다.
    // 포커스는 grid2/3 클릭 시 풀릴 수 있으므로 no_journal 로 부모행을 찾는다(포커스는 보조).
    const grid1Rows: any[] = dataSources.grid1 ?? [];
    // 선택된 담당자 헤더행: 캡처해둔 ref → no_journal 매칭 → 포커스 순으로 확보.
    const head =
      headRowRef.current ??
      grid1Rows.find(
        (r: any) => String(r.no_journal) === String(row.no_journal)
      ) ??
      gridRefs.current["grid1"].current?.focusedRowNode()?.data ??
      {};
    const nodeData = {
      yy: search.yy,
      no_week: search.no_week,
      cd_emp: row.cd_emp ?? head.cd_emp,
      nm_emp: head.nm_emp ?? row.nm_emp,
      // 업무영역은 더블클릭한 라인의 journal 값 1개여야 한다.
      // grid1(head)의 fg_domain 은 담당자당 1건이라 "1,2,3" 처럼 합쳐져 있어 쓰지 않는다.
      fg_domain: row.fg_domain ?? search.fg_domain,
      st_journal: row.st_journal ?? head.st_journal,
    };
    const target = useMenuStore
      .getState()
      .menuItems.find((m: any) => m.cd_menu === "SN_SFA_JOURNAL_REG_001");
    if (!target) {
      showDialog("alter", "주간업무일지작성 메뉴 권한이 없습니다.");
      return;
    }
    const filePath =
      `./${target.cd_module}/${target.cd_menu}/${target.cd_menu}`.toUpperCase();
    (window as any).onActivate(
      target.cd_menu,
      target.nm_menu,
      target.cd_module,
      filePath,
      target,
      target.nm_path,
      nodeData,
      "fg_drilldown.Search"
    );
  };

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: (data: any) => {
            headRowRef.current = data;
          },
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: { onRowDoubleClicked: onDrilldownToReg },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: { onRowDoubleClicked: onDrilldownToReg },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="40%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            // 조회 SQL 이 담당자당 1건으로 묶어 오고, 항목 사이는 chr(10) 이다 → 줄바꿈으로 표시한다.
            etcProps={{
              rowSelection: "single",
              title: "주간업무보고",
              wrapText: true,
            }}
            events={grids.grid1.events}
            // 상단이 담당자당 1건으로 묶여 오므로 하위 그리드도 담당자 기준으로 필터한다.
            // 주차는 조회조건에서 이미 고정돼 있어 cd_emp 하나로 충분하다.
            // (일지 기준이면 담당자에 업무영역이 다른 일지가 2건 이상일 때 첫 건만 걸린다.)
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["cd_emp"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="59%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="49.5%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "single", title: "전주실적" }}
                events={grids.grid2.events}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0">
              <SnGrid
                ref={gridRefs.current["grid3"]} // Grid Name
                columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                rowData={grids.grid3.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "금주계획" }}
                events={grids.grid3.events}
              />
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_SFA_JOURNAL_REP_001;
